import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faAngleDown, faSpinner, faCheck, faUndo, faRedo } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { appConfiguration } from '../../appConfiguration.ts';
import { BeamlineContext } from '../../beamlineProvider.tsx';
import { SampleSetsContext, SampleSetsLoadingState } from '../../sampleSetsProvider.tsx';
import { ScanTypeSlug, ParameterChoice } from '../../scanTypes.ts';
import { SampleConfiguration, SampleConfigurationSet, SampleConfigurationField, SampleConfigurationFieldSelection } from '../../sampleSets.ts';
import { Coordinates, EditableTableCellSettings, EditableTable } from '../../components/editableTable.tsx';
import { CellValidationStatus, CellValidationResult } from '../../components/editableTableCell/cellDto.ts';
import { EditAsChoice } from './../../api/api.ts';
import { SampleTableClipboardContent } from './sampleClipboard.ts';
import AddSamples from './addSamples.tsx';
import MoveSamples from './moveSamples.tsx';
import DeleteSamples from './deleteSamples.tsx';
import { Uid, truthyJoin, sortWithNumberParsing } from '../../utils.tsx';


interface SampleTableProps {
  setslug: string;
}

enum SyncState { Idle, Pending, Requested, Failed, Completed };

// It's a magic number I know, and that's annoying, but this is the number of
// standard non-parameter fields that are always displayed on the left side of the table.
const FIXED_COLUMN_COUNT = 3;


const SampleTable: React.FC<SampleTableProps> = (props) => {

  const setSlug = props.setslug;

  const beamlineContext = useContext(BeamlineContext);
  const sampleSetsContext = useContext(SampleSetsContext);

  const [sortedSampleSlugs, setSortedSampleSlugs] = useState<string[]>([]);
  const [selectedSampleSlugs, setSelectedSampleSlugs] = useState<string[]>([]);

  const [tableSortColumn, setTableSortColumn] = useState<SampleConfigurationFieldSelection>({ field: SampleConfigurationField.Name, parameter: null });
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(false);

  const [syncTimer, setSyncTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [syncState, setSyncState] = useState<SyncState>(SyncState.Idle);
  const [tableHelpDisclosed, setTableHelpDisclosed] = useState<boolean>(false);


  useEffect(() => {
    if ((setSlug === undefined) || (!setSlug.trim())) { return; }

    if (sampleSetsContext.loadingState != SampleSetsLoadingState.Succeeded) { return; }

    const thisSet = sampleSetsContext.sets.getBySlug(setSlug.trim());
    if (thisSet === undefined) { return; }

    const sortedSampleSlugs = sortSampleSlugs(thisSet, tableSortColumn, tableSortReverse);
    setSortedSampleSlugs(sortedSampleSlugs);

  }, [setSlug, sampleSetsContext.changeCounter, sampleSetsContext.loadingState]);


  function sortSampleSlugs(thisSet: SampleConfigurationSet, field: SampleConfigurationFieldSelection, reverse: boolean): string[] {
    const samples = thisSet.all();
    const parametersById = thisSet.scanTypesReference.parametersById;
    var sorted:SampleConfiguration[] = [];
    switch (field.field) {
      case SampleConfigurationField.Description:
        sorted = samples.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        break;
      case SampleConfigurationField.ScanType:
        sorted = samples.sort((a, b) => {
          const aName = beamlineContext.scanTypes.typesBySlug.get(a.scanTypeSlug)?.name || "";
          const bName = beamlineContext.scanTypes.typesBySlug.get(b.scanTypeSlug)?.name || "";
          return sortWithNumberParsing(aName, bName, reverse)
        });
        break;
      default:  // Default is to sort by Name
        sorted = samples.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        break;
      case SampleConfigurationField.Parameter:
        if (field.parameter !== null) {
          sorted = samples.sort((a, b) => {
            const aValue = a.parameterValuesByName.get(parametersById.get(field.parameter!)?.name || "") || "";
            const bValue = b.parameterValuesByName.get(parametersById.get(field.parameter!)?.name || "") || "";
            return sortWithNumberParsing(aValue, bValue, reverse);
          });
        }
        break;
      }
    return sorted.map((s) => s.slug);
  }


  function clickedTableSortHeader(f:SampleConfigurationFieldSelection) {
    const thisSet = sampleSetsContext.sets.getBySlug(setSlug!.trim());
    if (thisSet === undefined) { return; }

    if ((f.field == tableSortColumn.field) && (f.parameter == tableSortColumn.parameter)) {
      const sortedSampleSlugs = sortSampleSlugs(thisSet, tableSortColumn, !tableSortReverse);
      setTableSortReverse(!tableSortReverse);
      setSortedSampleSlugs(sortedSampleSlugs);
    } else  {
      const sortedSampleSlugs = sortSampleSlugs(thisSet, f, false);
      setTableSortColumn(f);
      setTableSortReverse(false);
      setSortedSampleSlugs(sortedSampleSlugs);
    }
  }


  function onTableHelpShow() {
    setTableHelpDisclosed(true);
  }

  function onTableHelpHide() {
    setTableHelpDisclosed(false);
  }


  const thisSet = sampleSetsContext.sets.getBySlug(setSlug!.trim())
  if (!thisSet) {
    return (<div></div>)
  }

  // For the standard "scan type" column, which uses the autocomplete widget
  const scanTypesAsChoices: ParameterChoice[] =
    beamlineContext.scanTypes.typeSlugsInDisplayOrder.map((slug) => {
      const scanType = beamlineContext.scanTypes.typesBySlug.get(slug);
      return { name: scanType?.name || "", description: scanType?.description || "" }
    });


  const displayedParameterIds = thisSet.relevantParameters.filter((p) => beamlineContext.scanTypes.parametersById.has(p));
  const displayedParameters = displayedParameterIds.map((p) => beamlineContext.scanTypes.parametersById.get(p)!);

  function tableOnCopy(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {

    const c:SampleTableClipboardContent = new SampleTableClipboardContent();

    var sampleRows = [];
    for (var y = rectLower.y; y <= rectHigher.y; y++) {
      sampleRows.push(thisSet!.samplesBySlug.get(sortedSampleSlugs[y])!);
    }

    // The first FIXED_COLUMN_COUNT columns of the table always represent these fields in order:
    const fields = [SampleConfigurationField.Name, SampleConfigurationField.Description, SampleConfigurationField.ScanType];
    var selectedFields = [];
    // If the selection overlaps those columns, we push the relevant field names. 
    var lowX = rectLower.x;
    while (lowX <= Math.min(rectHigher.x, FIXED_COLUMN_COUNT-1)) {
      selectedFields.push(fields[lowX]);
      lowX++;
    }

    var selectedParameters:Uid[] = [];
    // If the selection overlaps those columns, we push the relevant field names. 
    var lowX = Math.max(rectLower.x, FIXED_COLUMN_COUNT);
    while (lowX <= rectHigher.x) {
      selectedParameters.push(displayedParameterIds[lowX-FIXED_COLUMN_COUNT]);
      lowX++;
    }
    const selectedParameterNames = selectedParameters.map((p) => beamlineContext.scanTypes.parametersById.get(p)?.name || "").filter((n) => n != "");

    c.fromTable(sampleRows, selectedFields, selectedParameterNames);
    c.sendToClipboard(event);
  }


  function tableOnPaste(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {

    // The rect of the current selection.  We may re-form this in the process of pasting.
    var upperLeftX = rectLower.x;
    var upperLeftY = rectLower.y;
    var lowerRightX = rectHigher.x;
    var lowerRightY = rectHigher.y;

    const c:SampleTableClipboardContent = new SampleTableClipboardContent();

    c.fromClipboardPasteEvent(event);
    // If we didn't get any content, give up
    if (c.isEmpty()) { return; }

    // Were there no columns selected (whether fields or parameters),
    // as we would get from a copy of raw text?  (This will influence pasting behavior.)
    const clipboardColumnCount = c.selectedFields.length + c.selectedParameterNames.length; 

    const pasteValues = c.asGridOfValues();

    var editedConfigs = [];

    // If we have multiple rows of data to use for pasting, we should stop pasting when we run out of them.
    var stopAtRow = upperLeftY + pasteValues.length;
    // If we are pasting to a selection that's more than one row, we should stop pasting when we hit the bottom of the selection,
    // regardless of how many rows are on the clipboard.
    if (lowerRightY > upperLeftY) {
      stopAtRow = lowerRightY + 1;
      // And if we're also pasting multiple rows of data, we should stop at the end of them,
      // or the end of the selection, whichever is smaller.
      // (If we're just pasting one row, we can duplicate the values for the whole selection.)
      if (pasteValues.length > 1) {
        stopAtRow = Math.min(stopAtRow, upperLeftY + pasteValues.length);
      }
    }
    // We should always stop pasting if we reach the bottom of the table.
    stopAtRow = Math.min(stopAtRow, sortedSampleSlugs.length);

    // Typically we should paste as many columns as we had selected in the clipboard copy.
    var stopAtColumn = upperLeftX + clipboardColumnCount;
    // But if we are pasting to a selection that's more than one column, we should stop at the end of the selection,
    // regardless of how many columns are available.
    if (lowerRightX > upperLeftX) {
      stopAtColumn = lowerRightX + 1;
      // And if we're also pasting multiple columns of data, we should stop at the end of them,
      // or the end of the selection, whichever is smaller.
      // (If we're just pasting one column, we can duplicate the values for the whole selection.)
      if (clipboardColumnCount > 1) {
        stopAtColumn = Math.min(stopAtColumn, upperLeftX + clipboardColumnCount);
      }
    }
    // On top of any other limits, we should always stop pasting if we reach the right side of the table.
    stopAtColumn = Math.min(stopAtColumn, FIXED_COLUMN_COUNT + displayedParameters.length);

    var y = 0;
    while (upperLeftY+y < stopAtRow) {

      var editedConfig = thisSet!.samplesBySlug.get(sortedSampleSlugs[upperLeftY+y])!.clone();
      const thisScanType = beamlineContext.scanTypes.typesBySlug.get(editedConfig.scanTypeSlug)!;
      const allowedParameters = new Set(thisScanType.parameterSettingsById.keys());
      const currentClipboardRow = pasteValues.length == 1 ? pasteValues[0] : pasteValues[y];

      var x = 0;
      while (upperLeftX+x < stopAtColumn) {
        var pasteValue = currentClipboardRow.length == 1 ? currentClipboardRow[0] : currentClipboardRow[x];
        if (pasteValue === null) { pasteValue = ""; }

        // Name
        if ((upperLeftX+x === 0) && (pasteValue !== "")) {  // Don't allow blank names
          editedConfig.name = pasteValue.replace(/[^A-Za-z0-9\-_]/g, "_");

        // Description
        } else if (upperLeftX+x === 1) {
          editedConfig.description = pasteValue;

        // Scan Type
        } else if (upperLeftX+x === 2) {
          const toScanTypeSlug = beamlineContext.scanTypes.typeSlugsByName.get(pasteValue) || (pasteValue as ScanTypeSlug);
          const newScanType = beamlineContext.scanTypes.typesBySlug.get(toScanTypeSlug);
          // If the text doesn't resolve to a known Scan Type name, skip all this.
          // We accept invalid values in most columns but accepting one in ScanType would make very ambiguous behavior,
          // since it affects which columns are displayed in the table.
          if (newScanType) {
            editedConfig.scanTypeSlug = toScanTypeSlug;
            newScanType!.parameterSettingsById.forEach((s, id) => {
              // Set any missing parameters to defaults.
              const p = beamlineContext.scanTypes.parametersById.get(id);
              if (!p) { return; }
              if (!editedConfig.parameterValuesByName.has(p.name)) {
                const defaultValue = s.default ?? p.standardDefault ?? "";
                editedConfig.parameterValuesByName.set(p.name, defaultValue);
              }
            });
          }
        // Scan parameters
        } else if ((upperLeftX+x >= FIXED_COLUMN_COUNT) && (((upperLeftX+x)-FIXED_COLUMN_COUNT) < displayedParameters.length)) {
          const paramType = displayedParameters[(upperLeftX+x)-FIXED_COLUMN_COUNT];
          const settings = thisScanType.parameterSettingsById.get(paramType.id);
          // readOnly may not be defined, so this could effectively still be true | false | undefined.
          const readOnly = settings?.read_only;
          const unused = !allowedParameters.has(paramType.id);
          if (!readOnly && !unused) {
            editedConfig.parameterValuesByName.set(paramType.name, pasteValue);
          }
        }
        x++;
      }

      editedConfigs.push(editedConfig);
      y++;
    }

    thisSet!.replaceWithHistory(editedConfigs);
    sampleSetsContext.changed();
    contentChanged();
  }


  // Add the vector to the given currentPosition until it points to a table cell that is
  // valid for editing, then return that position, or return null if we run off the edge of the table.
  function isCellValidForEditing(coordinates: Coordinates): boolean {
    // At this point we know the cell we're looking at is within the table.
    if (coordinates.x < FIXED_COLUMN_COUNT) {
      // The field cells are assumed to be always editable.
      return true;
    }
    // The only thing to ask now is whether the cell is for a ScanParameterType that's
    // _actually_used_ by the ScanType set by that row's SampleConfiguration.
    const thisParameter = displayedParameters[coordinates.x - FIXED_COLUMN_COUNT];
    const thisConfig = thisSet!.samplesBySlug.get(sortedSampleSlugs[coordinates.y]);

    // Get the set of the parameter Ids that are valid for the ScanType, and see if this cell's parameter is in it.
    const allowedParameterIdSet = new Set(beamlineContext.scanTypes.typesBySlug.get(thisConfig!.scanTypeSlug)!.parameterSettingsById.keys());

    if (allowedParameterIdSet.has(thisParameter.id)) {
      return true;
    }
    // If it's not in the set, we need to keep looking.
    return false;
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // and used when rendering the table,
  // that validates the given input value based on its cell's x,y location in the table.
  // It relies on the displayedParameters constant, calculated just above.
  function cellValidator(x: number, y: number, value: string): CellValidationResult {

    const genericSuccess = { status: CellValidationStatus.Success, message: null };

    // Validate Name
    if (x === 0) {
      if (value == "") {
        return { status: CellValidationStatus.Failure, message: "Name cannot be blank." };
      } else {
        const configsMatchingName = sortedSampleSlugs.filter((slug) => {
                const c = thisSet!.samplesBySlug.get(slug);
                if (!c) { return false; }
                return c.name == value;
              });
        if (configsMatchingName.length > 1) {
          return { status: CellValidationStatus.Failure, message: "Name must be unique on bar." };
        } else if (value.search(/[^A-Za-z0-9\-_]/g) >= 0) {
          return { status: CellValidationStatus.Failure, message: "Name must consist only of letters, numbers, underscore, or dash." };
        }
      }
      return genericSuccess;

    // Validate Description
    } else if (x === 1) {
      // Description can be anything
      return genericSuccess;

    // Validate Scan Type
    } else if (x === 2) {
      if (beamlineContext.scanTypes.typesBySlug.has(value as ScanTypeSlug)) {
        return genericSuccess;
      }
      return { status: CellValidationStatus.Failure, message: "Must be an identifier for a Scan Type." };

    // Validate scan parameters
    } else if ((x >= FIXED_COLUMN_COUNT) && ((x-FIXED_COLUMN_COUNT) < displayedParameters.length)) {

      const paramType = displayedParameters[x - FIXED_COLUMN_COUNT];
      // If we're editing JSON, custom validator functions are not compatible.
      if (paramType.editAs == EditAsChoice.JSON) {

        if (value === "" || value === undefined) {
          return genericSuccess;
        }
        if (typeof value === "string") {
          try {
            JSON.parse(value);
          } catch (e) {
            return { status: CellValidationStatus.Failure, message: "Invalid JSON: " + (e as Error).message };
          }
        }
      } else if (paramType.validatorFunction !== undefined) {
        const result = paramType.validatorFunction(value);
        if (result !== null) {
          return { status: CellValidationStatus.Failure, message: result };
        }
      }
      // If no validator exists, or the validator returned null, the value is good.
      return genericSuccess;
    }
    return { status: CellValidationStatus.Failure, message: "Error: Cannot find this parameter!" };
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // that saves the given input value to the SampleConfiguration indicated by the
  // table cell at the given x,y location.
  // It relies on the displayedParameters constant, calculated just above.
  function cellSave(x: number, y: number, newValue: string): CellValidationResult {
    const thisConfig = thisSet!.samplesBySlug.get(sortedSampleSlugs[y]);
    if (!thisConfig) {
      return { status: CellValidationStatus.Failure, message: "Error: SampleConfiguration does not exist!" }
    }

    var editedConfig = thisConfig.clone();

    // Name
    if (x === 0) {
      editedConfig.name = newValue.replace(/[^A-Za-z0-9\-_]/g, "_");

    // Description
    } else if (x === 1) {
      editedConfig.description = newValue;

    // Scan Type
    } else if (x === 2) {
      const toScanTypeSlug = beamlineContext.scanTypes.typeSlugsByName.get(newValue) || (newValue as ScanTypeSlug);
      const newScanType = beamlineContext.scanTypes.typesBySlug.get(toScanTypeSlug);
      // If the text doesn't resolve to a known Scan Type name, skip all this.
      // We accept invalid values in most columns but accepting one in ScanType would make very ambiguous behavior,
      // since it affects which columns are displayed in the rest of the table.
      if (newScanType) {
        editedConfig.scanTypeSlug = toScanTypeSlug;
        newScanType!.parameterSettingsById.forEach((s, id) => {
          const p = beamlineContext.scanTypes.parametersById.get(id);
          if (!p) { return; }
          // Set any missing parameters to defaults.
          if (!editedConfig.parameterValuesByName.has(p.name)) {
            var defaultValue = s.default ?? p.standardDefault ?? "";
            // This is hacky, and dealt with in the sampleSets context,
            // where parameters with an id of zero are created during the next database undo history sync.
            editedConfig.parameterValuesByName.set(p.name, defaultValue);
          }
        });
      }
    // Validate scan parameters
    } else if ((x >= FIXED_COLUMN_COUNT) && ((x-FIXED_COLUMN_COUNT) < displayedParameters.length)) {
      const paramType = displayedParameters[x - FIXED_COLUMN_COUNT];
      editedConfig.parameterValuesByName.set(paramType.name, newValue);
    }

    thisSet!.replaceWithHistory([editedConfig]);
    sampleSetsContext.changed();
    contentChanged();

    return { status: CellValidationStatus.Success, message: null };
  }


  function tableRowSelectionChanged(rowIndexes: number[]) {
    var newSelectedSampleSlugs = rowIndexes.map((i) => {
      return sortedSampleSlugs[i];
    });
    setSelectedSampleSlugs(newSelectedSampleSlugs);
  }


  function clickedUndo() {
    thisSet!.undo();
    sampleSetsContext.changed();
    contentChanged();
  }


  function clickedRedo() {
    thisSet!.redo();
    sampleSetsContext.changed();
    contentChanged();
  }


  function contentChanged() {
    if (syncTimer) { clearTimeout(syncTimer); }
    setSyncTimer(setTimeout(() => syncTimerExpired(), 1500));
    if (syncState != SyncState.Requested) {
      setSyncState(SyncState.Pending);
    }
  }


  async function syncTimerExpired() {
    // If another save is pending while the previous one is still
    // in progress (requested), we renew the timer and exit, until the previous
    // one reports either Completed or Failed.
    if (syncState == SyncState.Requested) {
      setSyncTimer(setTimeout(() => syncTimerExpired(), 1000));
      return;
    }

    setSyncTimer(null);
    const changes = thisSet!.getPendingChanges();
    if (!changes) {
      setSyncState(SyncState.Idle);
      return;
    }

    setSyncState(SyncState.Requested);

    const saveCalls = changes.changes.additions.map((e) => {
      const c = e as SampleConfiguration;
      return c.writeToServer();
    });
    // We used to form a 'deleteCalls' as well, and concat it
    // with 'saveCalls', but now deletion is a manual process only,
    // so undo/redo actions do not perform it.

    Promise.all(saveCalls).then((responses) => {
      if (responses.every((r) => r.success)) {
        setSyncState(SyncState.Completed);
        thisSet!.catchUpToEdit(changes.index);
      } else {
        setSyncState(SyncState.Failed);
      }
    });
  }


  function renderHeaderCell(x: number): React.JSX.Element {

    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    if (x === 0) {
      return (<th key="name"
              className={truthyJoin("sortable", (tableSortColumn.field == SampleConfigurationField.Name) && sortDirectionStyle)}
              onClick={ () => clickedTableSortHeader({ field: SampleConfigurationField.Name, parameter: null }) }
              scope="col">Name</th>);
    } else if (x === 1) {
      return (<th key="description"
              className={truthyJoin("sortable", (tableSortColumn.field == SampleConfigurationField.Description) && sortDirectionStyle)}
              onClick={ () => clickedTableSortHeader({ field: SampleConfigurationField.Description, parameter: null }) }
              scope="col">Description</th>);
    } else if (x === 2) {
      return (<th key="scantype"
              className={truthyJoin("sortable", (tableSortColumn.field == SampleConfigurationField.ScanType) && sortDirectionStyle)}
              onClick={ () => clickedTableSortHeader({ field: SampleConfigurationField.ScanType, parameter: null }) }
              scope="col">Scan Type</th>);
    }

    const p = displayedParameters[x - FIXED_COLUMN_COUNT];
    return (
        <th key={p.id}
            className={truthyJoin("sortable", ((tableSortColumn.field == SampleConfigurationField.Parameter) && (tableSortColumn.parameter == p.id)) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader({ field: SampleConfigurationField.Parameter, parameter: p.id }) }
            scope="col">{ p.name }</th>
      )
  }


  function getTableCellSettings(coordinates: Coordinates): EditableTableCellSettings {
    const sample = thisSet!.samplesBySlug.get(sortedSampleSlugs[coordinates.y]);
    if (!sample) {
      return {
        key: `${sampleSetsContext.changeCounter}-invalid`,
        isInvalid: false,
        isReadOnly: true,
        isUnused: true,
        value: null,
      };
    }
    const thisScanType = beamlineContext.scanTypes.typesBySlug.get(sample.scanTypeSlug)!;

    if (coordinates.x === 0) {
      const nameValidationResult = cellValidator(0, coordinates.y, sample.name);
      const nameInvalid = nameValidationResult.status == CellValidationStatus.Success ? false : true;

      return {
        key: `${sample.slug}-${sampleSetsContext.changeCounter}-name`,
        isInvalid: nameInvalid,
        isReadOnly: false,
        isUnused: false,
        value: sample.name,
      };
    } else if (coordinates.x === 1) {
      const descriptionValidationResult = cellValidator(1, coordinates.y, sample.description);
      const descriptionInvalid = descriptionValidationResult.status == CellValidationStatus.Success ? false : true;

      return {
        key: `${sample.slug}-${sampleSetsContext.changeCounter}-description`,
        isInvalid: descriptionInvalid,
        isReadOnly: false,
        isUnused: false,
        value: sample.description,
      };
    } else if (coordinates.x === 2) {
      return {
        key: `${sample.slug}-${sampleSetsContext.changeCounter}-scantype`,
        isInvalid: false,
        isReadOnly: false,
        isUnused: false,
        value: thisScanType.name,
        description: "Scan Type to use for this sample.",
        choices: scanTypesAsChoices
      }
    }

    const p = displayedParameters[coordinates.x - FIXED_COLUMN_COUNT];
    const parameterSettings = thisScanType.parameterSettingsById.get(p.id);
    const paramValue = sample.parameterValuesByName.get(p.name) ?? "";
    const validationResult = cellValidator(coordinates.x, coordinates.y, paramValue);
    const validValue = validationResult.status == CellValidationStatus.Success ? true : false;
    const allowedParameters = new Set(thisScanType.parameterSettingsById.keys());

    const settings:EditableTableCellSettings = {
      key: `${sample.slug}-${sampleSetsContext.changeCounter}-${p.id}`,
      isInvalid: !validValue,
      isReadOnly: parameterSettings?.read_only ?? false,
      isUnused: !allowedParameters.has(p.id),
      description: p.description,
      value: paramValue,
    }

    if ((p?.editAs == EditAsChoice.Pulldown) && p?.choices) {
      settings.choices = p.choices;
    } else if ((p?.editAs == EditAsChoice.Textbox) || (p?.editAs == EditAsChoice.JSON)) {
      settings.useModal = true;
      settings.modalTitle = `${p.name} for sample ${sample.name}`;
    }
    return settings;
  }


  const editableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    getTableCellSettings: getTableCellSettings,
    isCellValidForEditing: isCellValidForEditing,
    cellValidate: cellValidator,
    cellSave: cellSave,
    onCopy: tableOnCopy,
    onPaste: tableOnPaste,
    rowSelectionChanged: tableRowSelectionChanged
  };


  // Construct a message reporting the sync status between this page and the server

  var syncStatusMessage: React.JSX.Element | null = null;
  if (syncState == SyncState.Requested) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faSpinner} spin={true} /> Saving Changes</p>); 
  } else if (syncState == SyncState.Completed) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faCheck} /> Changes Saved</p>);
  } else if (syncState == SyncState.Failed) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faExclamationTriangle} color="darkred" /> Error Saving! Are you logged in?</p>);
  }


  return (
    <>

      <nav className="level">

        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5"><strong>{ sortedSampleSlugs.length }</strong> samples</p>
          </div>
          <div className="level-item">
            <AddSamples />
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
              { syncStatusMessage }
          </div>
          <div className="level-item">
            <div className="field has-addons">
              <p className="control">
                <button className="button" onClick={ clickedUndo } title="Undo" disabled={!thisSet!.canUndo()}>
                  <FontAwesomeIcon icon={faUndo} />
                </button>
              </p>
              <p className="control">
                <button className="button" onClick={ clickedRedo } title="Redo" disabled={!thisSet!.canRedo()}>
                  <FontAwesomeIcon icon={faRedo} />
                </button>
              </p>
            </div>
          </div>
          { selectedSampleSlugs.length > 0 && (
            <div className="level-item">
              <div className="dropdown is-right is-hoverable">
                <div className="dropdown-trigger">
                  <button className="button is-small" aria-haspopup="true" aria-controls="dropdown-menu-howto">
                    <p className="subtitle is-6">{ selectedSampleSlugs.length } selected sample{selectedSampleSlugs.length > 1 ? 's' : ''}</p>
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faAngleDown} />
                    </span>
                  </button>
                </div>
                <div className="dropdown-menu" id="dropdown-menu-howto" role="menu">
                  <div className="dropdown-content">
                    <MoveSamples selection={selectedSampleSlugs} />
                    <DeleteSamples selection={selectedSampleSlugs} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </nav>

      <div className="block" style={{ overflowX: "auto" }}>
        { sortedSampleSlugs.length == 0 ? (
          !tableHelpDisclosed ? (
              <nav className="level">
                <div className="level-left">
                  <div className="level-item">
                    <p>(You might want to check out this brief</p>
                  </div>
                  <div className="level-item">
                    <button className="button is-success" onClick={ onTableHelpShow }>Tutorial</button>
                  </div>
                  <div className="level-item">
                    <p>on sample editing.)</p>
                  </div>
                </div>
              </nav>
            ) : (        
              <div className="modal is-active">
                <div className="modal-background"></div>
                <div className="modal-card">
                  <header className="modal-card-head">
                    <p className="modal-card-title">How To Edit Samples</p>
                    <button className="delete" aria-label="close" onClick={ onTableHelpHide }></button>
                  </header>
                  <section className="modal-card-body">
                    <video src={`${appConfiguration.config.baseUrl}/static/sample_table_video_tutorial.mp4`} controls={true} autoPlay={true} loop={true} muted={true} playsInline={true}></video>                    
                  </section>
                </div>
              </div>
            )
        ) : (
          <EditableTable
              rowIdentifiers={ sortedSampleSlugs }
              columnCount={ FIXED_COLUMN_COUNT + displayedParameters.length }
              displayRowSelectionBoxes={ true }
              tableFunctions={ editableTableFunctions }
            />
        )}
      </div>
    </>
  )
}

export default SampleTable
